import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../../app/store";
import { getPeriodRange } from "common/utils/helpers";
import { PERIOD } from "common/utils/consts";

interface OrdersState {
  chartData?: {
    date: string;
    value: number;
  }[];
  filters: {
    vehicleCategory: number[];
    period?: number;
    vehicleType: number[];
    dateFrom?: string;
    dateTo?: string;
    searchText: string;
  };
}

const initialState: OrdersState = {
  filters: {
    vehicleCategory: [],
    vehicleType: [],
    period: PERIOD.MONTH,
    searchText: "",
    ...getPeriodRange(PERIOD.MONTH),
  },
};

const ordersSlice = createSlice({
  name: "administrator_orders",
  initialState,
  reducers: {
    setChartData(
      state,
      action: PayloadAction<
        {
          date: string;
          value: number;
        }[]
      >
    ) {
      state.chartData = action.payload;
    },
    setFilterVehicleCategory(state, action: PayloadAction<number>) {
      let currentVehicleCategories = state.filters.vehicleCategory;
      if (currentVehicleCategories.includes(action.payload)) {
        state.filters.vehicleCategory = currentVehicleCategories.filter(
          (el) => el !== action.payload
        );
      } else {
        state.filters.vehicleCategory = [
          action.payload,
          ...currentVehicleCategories,
        ];
      }
      state.filters.vehicleType = [];
    },
    setFilterVehicleType(state, action: PayloadAction<number>) {
      let currentVehicleTypes = state.filters.vehicleType;
      if (currentVehicleTypes.includes(action.payload)) {
        state.filters.vehicleType = currentVehicleTypes.filter(
          (el) => el !== action.payload
        );
      } else {
        state.filters.vehicleType = [action.payload, ...currentVehicleTypes];
      }
    },
    // DatePicker присылает null при монтировании и очистке: при активной вкладке
    // периода такие вызовы не должны сбрасывать выбранный период
    setDateFrom(state, action: PayloadAction<Undefinable<string>>) {
      if (!action.payload && state.filters.period !== undefined) return;
      state.filters.dateFrom = action.payload;
      state.filters.period = undefined;
    },
    setDateTo(state, action: PayloadAction<Undefinable<string>>) {
      if (!action.payload && state.filters.period !== undefined) return;
      state.filters.dateTo = action.payload;
      state.filters.period = undefined;
    },
    setFilterPeriod(state, action: PayloadAction<number>) {
      const range = getPeriodRange(action.payload);
      state.filters.period = action.payload;
      state.filters.dateFrom = range.dateFrom;
      state.filters.dateTo = range.dateTo;
    },
    setSearchText(state, action: PayloadAction<string>) {
      state.filters.searchText = action.payload;
    },
    resetFilter(state) {
      state.filters = {
        ...state.filters,
        vehicleCategory: [],
        vehicleType: [],
      };
    },
  },
});

export const orderFiltersSelector = (state: RootState) =>
  state.administrator.orders.filters;

export const {
  setFilterVehicleCategory,
  setFilterVehicleType,
  resetFilter,
  setFilterPeriod,
  setDateFrom,
  setDateTo,
  setSearchText,
  setChartData,
} = ordersSlice.actions;

export const orderReducer = ordersSlice.reducer;
